import axios from "axios";
import { config } from "../config/config";
import { alert } from "../utils/commonFunctions/alert";
import { logout } from "../utils/auth";
import { refreshAccessToken } from "../utils/tokenRefresh";
import { getDeviceId } from "../utils/deviceId";
import { getVisitorToken } from "../utils/visitorToken";
//import { commonFunctions } from "./utils/commonFunctions/";

const service = axios.create({
  baseURL: config.localApiUrl,
  timeout: 60000,
});

// Axios request interceptor - adds JWT authentication token to all outgoing requests
service.interceptors.request.use(
  (config) => {
    // Get JWT token from local storage
    const jwtToken = localStorage.getItem("token");

    // Attach token to Authorization header if available
    if (jwtToken) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${jwtToken}`,
      };
    }

    // Attach the device identifier on every call (session key for refresh/logout)
    const deviceId = getDeviceId();
    if (deviceId) {
      config.headers = {
        ...config.headers,
        "x-device-id": deviceId,
      };
    }

    const visitorToken = getVisitorToken();
    if (visitorToken) {
      config.headers = {
        ...config.headers,
        "x-visitor-token": visitorToken,
      };
    }

    return config;
  },
  (error) => {
    // Handle request errors
    return Promise.reject(error);
  }
);
// Flag to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

service.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - Token expired
    if (error?.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axios(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refreshToken");
      const accessToken = localStorage.getItem("token");
      // console.log("77---->REFRESH TOKEN PAYLOAD---->", { refreshToken });
      // console.log("78---->ACCESS TOKEN PAYLOAD---->", { accessToken });

      if (!refreshToken) {
        // No refresh token available - logout
        isRefreshing = false;
        alert.error("Session expired. Please login again.");
        setTimeout(() => {
          logout(true, "/login", true); // Skip API call - tokens already expired
        }, 500);
        return Promise.reject(error);
      }

      try {
        const refreshedTokens = await refreshAccessToken();

        if (refreshedTokens?.accessToken) {
          const newAccessToken = refreshedTokens.accessToken;
          const newRefreshToken = refreshedTokens.refreshToken;

          // Update tokens in localStorage
          localStorage.setItem("token", newAccessToken);
          // Only update refreshToken if a new one was returned; preserve existing otherwise
          if (newRefreshToken) {
            localStorage.setItem("refreshToken", newRefreshToken);
          }

          // Update Authorization header
          service.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken;
          originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;

          // Process queued requests with new token
          processQueue(null, newAccessToken);
          isRefreshing = false;

          // Retry the original request
          return service(originalRequest);
        }

        throw new Error("Token refresh failed");
      } catch (refreshError) {
        // Refresh token failed - logout
        processQueue(refreshError, null);
        isRefreshing = false;
        alert.error("Session expired. Please login again.");
        setTimeout(() => {
          logout(true, "/login", true); // Skip API call - tokens already expired
        }, 500);
        return Promise.reject(refreshError);
      }
    }

    // Handle other error status codes
    if (error?.response?.status) {
      if (error.response.status === 404) {
        // Not Found - let calling code handle
      }

      if (error.response.status === 500) {
        alert.error("Server error. Please try again later.");
      }

      if (error.response.status === 508) {
        alert.error("Request timeout. Please try again.");
      }
    }

    return Promise.reject(error);
  }
);

const authService = axios.create({
  baseURL: config.localApiUrl,
  timeout: 60000,
});

// Attach the device identifier so login / OTP land on the right session row
authService.interceptors.request.use(
  (config) => {
    const deviceId = getDeviceId();
    if (deviceId) {
      config.headers = {
        ...config.headers,
        "x-device-id": deviceId,
      };
    }

    const visitorToken = getVisitorToken();
    if (visitorToken) {
      config.headers = {
        ...config.headers,
        "x-visitor-token": visitorToken,
      };
    }

    return config;
  },
  (error) => Promise.reject(error)
);

authService.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    // Handle specific error status codes
    if (error?.response?.status) {
      if (error.response.status === 400 || error.response.status === 403) {
        // Let the calling thunk handle the error message
      }

      if (error.response.status === 404) {
        // Not Found
      }

      if (error.response.status === 500) {
        alert.error("Server error. Please try again later.");
      }

      if (error.response.status === 508) {
        alert.error("Request timeout. Please try again.");
      }
    }
    // Return the error data for thunk to handle
    return Promise.reject(error.response?.data || error);
  }
);

export { service as postLoginService };
export { authService as preLoginService };